// Onboarding Wizard Context

import React, { createContext, useContext, useState, useCallback } from 'react';
import type { OnboardingProgress, SelectedPayer } from '@/lib/onboarding/types';

interface OnboardingWizardContextValue {
  // Current state
  currentStep: number;
  clientId: string | null;
  progress: OnboardingProgress | null;
  
  // Draft data for current session (not yet saved)
  draftPayers: SelectedPayer[];
  setDraftPayers: (payers: SelectedPayer[]) => void;
  
  // Navigation
  goToStep: (step: number) => void;
  canGoToStep: (step: number) => boolean;
  
  // Validation
  isStepComplete: (step: number) => boolean;
  canActivate: () => boolean;
}

const OnboardingWizardContext = createContext<OnboardingWizardContextValue | null>(null);

interface OnboardingWizardProviderProps {
  children: React.ReactNode;
  clientId: string;
  progress: OnboardingProgress | null;
}

export function OnboardingWizardProvider({ 
  children, 
  clientId,
  progress 
}: OnboardingWizardProviderProps) {
  const [currentStep, setCurrentStep] = useState(progress?.current_step || 1);
  const [draftPayers, setDraftPayers] = useState<SelectedPayer[]>(
    progress?.selected_payers || []
  );

  const isStepComplete = useCallback((step: number): boolean => {
    if (!progress) return false;
    
    switch (step) {
      case 1: return progress.step1_complete;
      case 2: return progress.step2_complete;
      case 3: return progress.step3_complete;
      case 4: return progress.step4_complete;
      case 5: return progress.step5_complete;
      case 6: return progress.step6_complete;
      default: return false;
    }
  }, [progress]);

  const canGoToStep = useCallback((step: number): boolean => {
    if (step === 1) return true;
    // Can only go to a step if all previous steps are complete
    for (let i = 1; i < step; i++) {
      if (!isStepComplete(i)) return false;
    }
    return true;
  }, [isStepComplete]);

  const goToStep = useCallback((step: number) => {
    if (canGoToStep(step)) {
      setCurrentStep(step);
    }
  }, [canGoToStep]);

  const canActivate = useCallback((): boolean => {
    if (!progress) return false;
    return (
      progress.step1_complete &&
      progress.step2_complete &&
      progress.step3_complete &&
      progress.step4_complete &&
      progress.step5_complete
    );
  }, [progress]);

  return (
    <OnboardingWizardContext.Provider
      value={{
        currentStep,
        clientId,
        progress,
        draftPayers,
        setDraftPayers,
        goToStep,
        canGoToStep,
        isStepComplete,
        canActivate,
      }}
    >
      {children}
    </OnboardingWizardContext.Provider>
  );
}

export function useOnboardingWizard() {
  const context = useContext(OnboardingWizardContext);
  if (!context) {
    throw new Error('useOnboardingWizard must be used within OnboardingWizardProvider');
  }
  return context;
}
